import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import useTranslation from '../../hooks/useTranslation';
import SimpleButton from '../NSButton/Types/SimpleButton';

const InstallPrompt = () => {
  const T = useTranslation();
  const location = useLocation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

    useEffect(() => {
    // Solo mostrar en la página principal (/)
    if (location.pathname !== '/') {
      return;
    }

    // Verificar si ya está instalado
    const checkIfInstalled = () => {
      // Verificar si se ejecuta en modo standalone (app instalada)
      if (window.matchMedia('(display-mode: standalone)').matches) {
        setIsInstalled(true);
        return true;
      }

      // Verificar si está en iOS y tiene el meta tag apple-mobile-web-app-capable
      if (navigator.standalone === true) {
        setIsInstalled(true);
        return true;
      }

      return false;
    };

    // En localhost, siempre mostrar el prompt (para testing)
    const isLocalhost = window.location.hostname === 'localhost' || 
                       window.location.hostname === '127.0.0.1' ||
                       window.location.hostname.includes('localhost');

    if (isLocalhost) {
      // Simular el evento beforeinstallprompt para localhost
      const mockPrompt = {
        prompt: () => Promise.resolve({ outcome: 'dismissed' }),
        userChoice: Promise.resolve({ outcome: 'dismissed' })
      };
      setDeferredPrompt(mockPrompt);
      setShowInstallPrompt(true);
      return;
    }

    // Verificar si el usuario ya rechazó la instalación
    const hasUserRejected = localStorage.getItem('pwa-install-rejected');
    const hasUserAccepted = localStorage.getItem('pwa-install-accepted');

    // Solo mostrar si no está instalado y no ha rechazado anteriormente
    if (!checkIfInstalled() && !hasUserRejected && !hasUserAccepted) {
      // Escuchar el evento beforeinstallprompt
      const handleBeforeInstallPrompt = (e) => {
        e.preventDefault();
        setDeferredPrompt(e);
        setShowInstallPrompt(true);
      };

      // Escuchar el evento appinstalled
      const handleAppInstalled = () => {
        setIsInstalled(true);
        setShowInstallPrompt(false);
        localStorage.setItem('pwa-install-accepted', 'true');
        console.log('PWA instalada exitosamente');
      };

      window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.addEventListener('appinstalled', handleAppInstalled);

      // Limpiar event listeners
      return () => {
        window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        window.removeEventListener('appinstalled', handleAppInstalled);
      };
    }
  }, [location.pathname]);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      // En localhost, solo simular la acción
      const isLocalhost = window.location.hostname === 'localhost' || 
                         window.location.hostname === '127.0.0.1' ||
                         window.location.hostname.includes('localhost');
      
      if (isLocalhost) {
        console.log('Simulando instalación de PWA en localhost');
        setDeferredPrompt(null);
        setShowInstallPrompt(false);
        return;
      }

      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        localStorage.setItem('pwa-install-accepted', 'true');
        console.log('Usuario aceptó instalar la PWA');
      } else {
        localStorage.setItem('pwa-install-rejected', 'true');
        console.log('Usuario rechazó instalar la PWA');
      }
      
      setDeferredPrompt(null);
      setShowInstallPrompt(false);
    }
  };

  const handleDismiss = () => {
    // En localhost, no guardar preferencias
    const isLocalhost = window.location.hostname === 'localhost' || 
                       window.location.hostname === '127.0.0.1' ||
                       window.location.hostname.includes('localhost');
    
    if (!isLocalhost) {
      localStorage.setItem('pwa-install-rejected', 'true');
    }
    setShowInstallPrompt(false);
  };

  // Solo mostrar en la página principal (/)
  if (location.pathname !== '/') {
    return null;
  }

  // En localhost, siempre mostrar si hay prompt disponible
  const isLocalhost = window.location.hostname === 'localhost' || 
                     window.location.hostname === '127.0.0.1' ||
                     window.location.hostname.includes('localhost');
  
  // No mostrar si ya está instalado o no hay prompt disponible (excepto en localhost)
  if ((!isLocalhost && isInstalled) || !showInstallPrompt || !deferredPrompt) {
    return null;
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-[#2AAC9C] to-[#228C80] text-white p-4 shadow-lg">
      <div className="max-w-md mx-auto flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
            <span className="text-[#2AAC9C] text-xl">📱</span>
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-sm">
              {T('install_app_title') || 'Instalar SimulaRCP'}
            </h3>
            <p className="text-xs opacity-90">
              {T('install_app_description') || 'Acceso directo desde tu pantalla de inicio'}
            </p>
          </div>
        </div>
        
        <div className="flex gap-2">
          <SimpleButton
            text={T('install') || 'Instalar'}
            onClick={handleInstallClick}
            variant="white"
            className="flex-1"
          />
          <SimpleButton
            text={T('not_now') || 'Ahora no'}
            onClick={handleDismiss}
            variant="gray"
            className="flex-1"
          />
        </div>
      </div>
    </div>
  );
};

export default InstallPrompt; 